import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSearch, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import CommonTable from "../../../common/table/ComonTable";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";

const VisitorList = () => {
    const navigate = useNavigate();
    const [visitors, setVisitors] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters  
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState(""); // '', 'Pending', 'Approved', 'CheckedIn', 'CheckedOut'

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const employeeCode = storageService.getItem("employeeCode");

    const fetchVisitors = async () => {
        try {
            setLoading(true);
            const response = await apiServices({
                method: "GET",
                url: `/ResidentialInviteVisitor/CheckInInviteVisitor/${employeeCode}`
            });

            if (response?.success && response?.data?.data) {
                setVisitors(response.data.data);
            } else {
                toast.error(response?.message || "Failed to fetch visitors");
            }
        } catch (error) {
            console.error("Error fetching visitors:", error);
            toast.error("Error fetching visitors");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (employeeCode) {
            fetchVisitors();
        }
    }, [employeeCode]);

    const handleEdit = (row) => {
        navigate(`/zentres/visitor-management/visitor-form/${row.visitorId}`);
    };

    const handleLimitChange = (newLimit) => {
        setPageSize(newLimit);
        setCurrentPage(1);
    };

    const formatDateTime = (value) => {
        if (!value) return "—";
        try {
            return new Date(value).toLocaleString('en-IN', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        } catch {
            return value;
        }
    };

    // Apply filters
    const filteredVisitors = useMemo(() => {
        return visitors.filter(v => {
            const matchesSearch = !searchTerm ||
                v.visitorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                v.visitorMobileNumber?.includes(searchTerm);

            const matchesStatus = !statusFilter || v.status === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [visitors, searchTerm, statusFilter]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredVisitors.slice(start, start + pageSize);
    }, [filteredVisitors, currentPage, pageSize]);

    const columns = [
        { key: "no", label: "No", align: "center" },
        { key: "siteName", label: "Site", align: "left" },
        { key: "unitName", label: "Unit/Flat", align: "left" },
        { key: "visitorName", label: "Visitor Name", align: "left" },
        { key: "visitorMobileNumber", label: "Mobile", align: "left" },
        { key: "visitorEmailId", label: "Email", align: "left" },
        { key: "visitorLocation", label: "Location", align: "left" },
        { key: "PurposeOfVisit", label: "Purpose", align: "left" },
        { key: "createdDate", label: "Date", align: "left" },
        { key: "image", label: "Photo", align: "center" },
        { key: "status", label: "Status", align: "center" },
        { key: "actions", label: "Actions", align: "center" },
    ];

    const renderCell = (key, row, index) => {
        switch (key) {
            case "no":
                return <span className="text-[13px] font-medium text-[#64748B]">{index + 1 + (currentPage - 1) * pageSize}</span>;
            case "siteName":
                return <span className="text-[13px] font-medium text-[#64748B]">{row.siteName || "—"}</span>;
            case "unitName":
                return <span className="text-[13px] font-medium text-[#64748B]">{row.unitName || "—"}</span>;
            case "visitorName":
                return <span className="text-[13px] font-bold text-gray-800 tracking-tight">{row.visitorName || "—"}</span>;
            case "visitorMobileNumber":
                return <span className="text-[13px] font-medium text-[#64748B]">{row.visitorMobileNumber || "—"}</span>;
            case "visitorEmailId":
                return (
                    <span className="text-[13px] font-medium text-[#64748B] block max-w-[160px] truncate" title={row.visitorEmailId}>
                        {row.visitorEmailId || "—"}
                    </span>
                );
            case "visitorLocation":
                return <span className="text-[13px] font-medium text-[#64748B]">{row.visitorLocation || "—"}</span>;
            case "PurposeOfVisit":
                return <span className="text-[13px] font-medium text-[#64748B]">{row.PurposeOfVisit || "—"}</span>;
            case "createdDate":
                return <span className="text-[13px] font-medium text-[#64748B]">{formatDateTime(row.createdDate)}</span>;
            case "image":
                return (
                    <div className="flex justify-center">
                        <img
                            src={row.image || ""}
                            alt={row.visitorName}
                            style={{ width: 50, height: 50, borderRadius: 25, objectFit: 'cover' }}
                            className="border-2 border-gray-100 shadow-sm"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <div
                            className="hidden w-10 h-10 rounded-full bg-gray-100 items-center justify-center text-gray-400 text-xs font-bold border border-gray-200"
                        >
                            {(row.visitorName || 'V')[0].toUpperCase()}
                        </div>
                    </div>
                );
            case "status":
                const status = row.status || 'Pending';
                const statusStyles = {
                    'Approved': "bg-[#DCFCE7] text-[#166534]",
                    'CheckedIn': "bg-[#DBEAFE] text-[#1D4ED8]",
                    'CheckedOut': "bg-[#F1F5F9] text-[#64748B]",
                    'Pending': "bg-[#FEF9C3] text-[#D97706]",
                };
                return (
                    <span className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold shadow-sm ${statusStyles[status] || statusStyles['Pending']}`}>
                        {status}
                    </span>
                );
            case "actions":
                return null;
            default:
                return <span className="text-[13px] font-medium text-[#64748B]">{row[key] || "—"}</span>;
        }
    };

    const STATUS_TABS = ["", "Pending", "Approved", "CheckedIn", "CheckedOut"];
    const STATUS_LABELS = { "": "All", "Pending": "Pending", "Approved": "Approved", "CheckedIn": "Checked In", "CheckedOut": "Checked Out" };

    const headerAction = (
        <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative w-full sm:w-60">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                    type="text"
                    placeholder="Search by name or mobile..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-9 pr-4 py-2 border border-gray-200 bg-[#F8FAFC] rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 focus:bg-white transition-colors"
                />
            </div>

            {/* Status filter tabs */}
            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                {STATUS_TABS.map((val) => (
                    <button
                        key={val || "all"}
                        onClick={() => { setStatusFilter(val); setCurrentPage(1); }}
                        className={`px-3 py-2 text-xs font-bold transition-all whitespace-nowrap ${statusFilter === val
                            ? "bg-[#E31F26] text-white"
                            : "bg-white text-gray-500 hover:bg-gray-50"
                            }`}
                    >
                        {STATUS_LABELS[val]}
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto gap-6 pt-4 scrollbar-hide">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading="Visitor Management"
                subHeading="Manage your residential visitors and invitations"
                primaryBtnText="Invite Visitor"
                primaryBtnIcon={faUserPlus}
                PrimaryOnClick={() => navigate('/zentres/visitor-management/visitor-form/add')}
            />

            <div className="shrink-0 flex-1 flex flex-col bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <CommonTable
                    headers={columns}
                    headerAction={headerAction}
                    data={paginatedData.map((d, i) => ({ ...d, originalIndex: i }))}
                    renderCell={(key, row) => renderCell(key, row, row.originalIndex)}
                    pagination={{
                        pageNumber: currentPage,
                        pageSize,
                        totalRecords: filteredVisitors.length,
                        totalPages: Math.ceil(filteredVisitors.length / pageSize) || 1
                    }}
                    onPageChange={(page) => setCurrentPage(page)}
                    onLimitChange={handleLimitChange}
                    loading={loading}
                    onEdit={handleEdit}
                />
            </div>
        </div>
    );
};

export default VisitorList;
